import type { IdentityStore } from "../core/ports";

/** localStorageが持つ、visitorId保存に必要な最小インターフェース。 */
export interface VisitorIdStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** storageの取得自体が例外になる環境もテストできる遅延取得関数。 */
type StorageProvider = () => VisitorIdStorage;

interface IdentityMemory {
  visitorId?: string;
}

// 同一SDKから複数ウィジェットをマウントしても、ページ内では同じIDを共有する。
const sharedMemory: IdentityMemory = {};

/** 外部サイトとの保存キー契約。DevToolsで人間が確認できる固定名にする。 */
export const VISITOR_ID_STORAGE_KEY = "vote_visitor_id";

/**
 * メモリを第一級、localStorageをページ再読込用バックアップとして扱うIDストア。
 * storageの拒否・容量超過が起きても、メモリ更新を先に行うため当該ページでは継続できる。
 */
export class BrowserIdentityStore implements IdentityStore {
  public constructor(
    private readonly storageProvider: StorageProvider = () => window.localStorage,
    private readonly memory: IdentityMemory = sharedMemory,
  ) {}

  /** メモリを優先し、未読込時だけ永続値を取り込む。storage例外は未保持として扱う。 */
  public get(): string | undefined {
    if (this.memory.visitorId !== undefined) {
      return this.memory.visitorId;
    }

    try {
      const storedVisitorId = this.storageProvider().getItem(VISITOR_ID_STORAGE_KEY)?.trim();
      if (storedVisitorId !== undefined && storedVisitorId !== "") {
        this.memory.visitorId = storedVisitorId;
      }
    } catch {
      return undefined;
    }

    return this.memory.visitorId;
  }

  /** 採用IDを必ずメモリへ反映し、可能な場合だけ永続値も上書きして自己修復する。 */
  public set(visitorId: string): void {
    this.memory.visitorId = visitorId;
    try {
      this.storageProvider().setItem(VISITOR_ID_STORAGE_KEY, visitorId);
    } catch {
      // 永続化不能でも、ページ内の通信は直前に更新したメモリIDで継続する。
    }
  }
}
